import { ChallengeService } from '@duck/api-definitions'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useAvailableChallenges } from '@/hooks/useAvailableChallenges'
import { apis } from '../../modules/apiClients'
import { useBootChallengeWithWeapon, WeaponPickCancelledError } from '@/hooks/useBootChallengeWithWeapon'
import { useTar, type TranslationKey } from '@/modules'
import { availableChallengesQueryKeys } from '@/hooks/useAvailableChallenges'
import { queryClient } from '@/modules/queryClient'
import { ChallengeGridSkeleton } from './ChallengeGridSkeleton'
import { ChallengeCard } from './ChallengeCard'
import { AssignmentChallengeCard } from './AssignmentChallengeCard'
import { ChallengeFilterRail } from './ChallengeFilterRail'
import { ChallengeFilterPanel } from './ChallengeFilterPanel'
import { ResumeStrip } from './ResumeStrip'
import { ChallengeDetailDrawer, type DrawerItem } from './ChallengeDetailDrawer'
import { NoDataEmptyState, LoginRequiredEmptyState } from '../EmptyStates'
import { useChallengeBrowser, type GroupKey, type GroupIcon } from '@/hooks/useChallengeBrowser'
import {
  Button,
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@duck/ui'
import { BarChart3, ChevronDown, ClipboardList, Clock, Funnel, LayoutGrid, SearchX, Sparkles, type LucideIcon } from 'lucide-react'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { StandardLoader } from '../StandardLoader'
import { useStartChallenge } from '@/hooks/useStartChallenge'
import { useStartAssignment } from '@/hooks/useStartAssignment'
import { HelpMePickWizard } from './HelpMePick/HelpMePickWizard'
import { findChallengeBySlug } from './focusSlug.utils'
import { trackChallengeBrowse } from '@/modules/challengesAnalytics'

const GROUP_OPTIONS: { value: GroupKey; key: TranslationKey; Icon: LucideIcon }[] = [
  { value: 'time', key: 'challenges.groupByTime', Icon: Clock },
  { value: 'difficulty', key: 'challenges.groupByDifficulty', Icon: BarChart3 },
  { value: 'none', key: 'challenges.groupByNone', Icon: LayoutGrid },
]

// Shared category header: icon badge · title · pill count · trailing rule ·
// a chevron at the inline-end that collapses/expands the category.
const GroupHeading = ({ icon: Icon, iconClassName, title, count, collapsed, onToggle, collapseLabel }: {
  icon: GroupIcon
  iconClassName?: string
  title: string
  count: number
  collapsed: boolean
  onToggle: () => void
  collapseLabel: string
}) => (
  <div className="flex items-center gap-3">
    <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-border/70 bg-foreground/[0.04] text-foreground">
      <Icon className={`h-[18px] w-[18px] ${iconClassName ?? ''}`} />
    </span>
    <h2 className="text-2xl font-bold tracking-tight text-foreground">{title}</h2>
    <span className="grid h-6 min-w-[1.5rem] place-items-center rounded-full border border-border px-2 text-xs font-medium tabular-nums text-muted-foreground">
      {count}
    </span>
    <div className="h-px flex-1 bg-border/50" />
    <button
      type="button"
      onClick={onToggle}
      aria-label={collapseLabel}
      aria-expanded={!collapsed}
      className="grid h-7 w-7 flex-none place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
    >
      <ChevronDown className={`h-5 w-5 transition-transform ${collapsed ? '-rotate-90 rtl:rotate-90' : ''}`} />
    </button>
  </div>
)

export const AvailableChallengesPage = ({ focusSlug }: { focusSlug?: string } = {}) => {
  const [showLoading, setShowLoading] = useState(false)
  const [isRolling, setIsRolling] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [openAssignmentId, setOpenAssignmentId] = useState<string | null>(null)
  // When the page is opened at /challenges/<slug>, focus that task once the
  // available-challenges list has loaded: filter the grid to its name and open
  // its drawer. If the slug isn't in the user's list, open the drawer in an
  // "unavailable" state instead (login prompt when logged out). Guarded so it
  // fires exactly once per mount and never fights the user afterwards.
  const [focusUnavailable, setFocusUnavailable] = useState(false)
  const focusHandledRef = useRef(false)
  const { isAuthenticated, loggedInAction, openLoginDialog } = useAuth()
  const { t } = useTar()
  const routerNavigate = useNavigate()
  const { challenges, assignments, isLoading } = useAvailableChallenges()
  // When the user has assignments to do but no rollable/browsable challenges,
  // the browse controls (grouping, filters, help-me-pick, random) have nothing
  // to act on — keep them visible but faded and disabled so the page still
  // reads as the challenges page, just without live controls.
  const onlyAssignments = !isLoading && challenges.length === 0 && assignments.length > 0
  const bootChallenge = useBootChallengeWithWeapon()
  const browser = useChallengeBrowser(challenges)
  const startChallenge = useStartChallenge()
  const { requestStart: startAssignment, timeLimitDialog: assignmentTimeLimitDialog } = useStartAssignment()
  const { getSetting, setSetting } = useLocalSettings()
  // A /challenges/<slug> deep link opens with the filter panel closed for a
  // clean focused view, regardless of the persisted preference. The first time
  // the user toggles filters this override clears, so their preference resumes
  // (and we never overwrite it just by following a link).
  const [slugFiltersClosed, setSlugFiltersClosed] = useState(focusSlug !== undefined)
  const filtersOpen = slugFiltersClosed ? false : (getSetting('challengeFiltersOpen') ?? false)
  const toggleFilters = () => {
    trackChallengeBrowse('challenges:filter:panel', { open: !filtersOpen })
    setSlugFiltersClosed(false)
    setSetting('challengeFiltersOpen', !filtersOpen)
  }
  // Mobile filter sheet is controlled so "Clear filters" can dismiss it.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const toggleCollapsed = (key: string, axis: string) => {
    trackChallengeBrowse('challenges:category:toggle', { axis, category: key, collapsed: !collapsed.has(key) })
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  const ASSIGNMENTS_KEY = '__assignments__'

  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setShowLoading(true)
      }, 256)
      return () => clearTimeout(timeout)
    }
    setShowLoading(false)
  }, [isLoading])

  const handleRollChallenge = () => {
    loggedInAction('roll-challenge', async () => {
      setIsRolling(true)
      try {
        const rollResponse = await apis.challengeService.rollChallenge()
        const { taskId, allowedWeapons } = rollResponse.data

        const bootResponse = await bootChallenge({ taskId, allowedWeapons })
        const slug = bootResponse.data.slug

        if (slug) {
          sessionStorage.setItem('challenge_unbooted', slug)
          void queryClient.invalidateQueries({ queryKey: availableChallengesQueryKeys.all() })
          routerNavigate({ to: `/challenge/${slug}` })
        }
      } catch (err) {
        if (err instanceof WeaponPickCancelledError) return
        toast.error(t('challenges.failedToRollChallenge'), {
          duration: 4000
        })
      } finally {
        setIsRolling(false)
      }
    })
  }

  useEffect(() => {
    if (focusSlug === undefined || isLoading || focusHandledRef.current) return
    focusHandledRef.current = true
    const match = findChallengeBySlug(challenges, focusSlug)
    if (match !== undefined) {
      browser.setQuery(match.name)
      setOpenAssignmentId(null)
      setOpenId(browser.identity(match))
    } else {
      setFocusUnavailable(true)
    }
  }, [focusSlug, isLoading, challenges, browser])

  const byIdentity = useMemo(
    () => new Map(challenges.map((c) => [browser.identity(c), c])),
    [challenges, browser]
  )
  const openChallenge = openId !== null ? byIdentity.get(openId) ?? null : null
  const openAssignment = openAssignmentId !== null
    ? assignments.find((a) => a.assignmentId === openAssignmentId) ?? null
    : null

  // Card body clicks open the shared drawer. Opening one kind closes the other
  // so only a single item is ever in view.
  const openDrawer = (c: ChallengeService.AvailableChallenge) => { setOpenAssignmentId(null); setOpenId(browser.identity(c)) }
  const openAssignmentDrawer = (a: ChallengeService.AvailableAssignment) => { setOpenId(null); setOpenAssignmentId(a.assignmentId) }
  const closeDrawer = () => { setOpenId(null); setOpenAssignmentId(null); setFocusUnavailable(false) }

  // The drawer is shared: derive its item, ordering, and navigation from
  // whichever list (challenges or assignments) the open item belongs to.
  const assignmentOrder = assignments.map((a) => a.assignmentId)
  const drawerItem: DrawerItem | null = focusUnavailable
    ? { kind: 'unavailable', authenticated: isAuthenticated }
    : openAssignment
      ? { kind: 'assignment', assignment: openAssignment }
      : openChallenge
        ? { kind: 'challenge', challenge: openChallenge }
        : null
  const drawerOpen = openId !== null || openAssignmentId !== null || focusUnavailable
  const drawerPosition = focusUnavailable
    ? 0
    : openAssignmentId !== null
      ? assignmentOrder.indexOf(openAssignmentId) + 1
      : openId !== null
        ? browser.visibleOrder.indexOf(openId) + 1
        : 0
  const drawerTotal = focusUnavailable
    ? 0
    : openAssignmentId !== null
      ? assignmentOrder.length
      : browser.visibleOrder.length

  // The items on either side of the open one, so the drawer's swipe track can
  // render the real neighbours next to the current item (no gap mid-swipe).
  const neighbourItem = (offset: -1 | 1): DrawerItem | null => {
    if (focusUnavailable) return null
    if (openAssignmentId !== null) {
      const id = assignmentOrder[assignmentOrder.indexOf(openAssignmentId) + offset]
      const assignment = id === undefined ? undefined : assignments.find((a) => a.assignmentId === id)
      return assignment === undefined ? null : { kind: 'assignment', assignment }
    }
    if (openId !== null) {
      const id = browser.visibleOrder[browser.visibleOrder.indexOf(openId) + offset]
      const challenge = id === undefined ? undefined : byIdentity.get(id)
      return challenge === undefined ? null : { kind: 'challenge', challenge }
    }
    return null
  }
  const drawerPrev = neighbourItem(-1)
  const drawerNext = neighbourItem(1)

  const navigateDrawer = (direction: -1 | 1) => {
    if (openAssignmentId !== null) {
      const idx = assignmentOrder.indexOf(openAssignmentId)
      const next = idx + direction
      if (next >= 0 && next < assignmentOrder.length) setOpenAssignmentId(assignmentOrder[next])
      return
    }
    const idx = browser.visibleOrder.indexOf(openId ?? '')
    const next = idx + direction
    if (next >= 0 && next < browser.visibleOrder.length) setOpenId(browser.visibleOrder[next])
  }

  const startDrawerItem = () => {
    if (openAssignment) startAssignment(openAssignment)
    else if (openChallenge) startChallenge(openChallenge)
  }

  return (
    <div className="relative flex w-full flex-1 overflow-hidden">
      {/* Ambient lighting — two faint, high glows give the surface a little depth
          without drawing attention: a primary up in the top-right corner and a
          fainter one drifting toward center. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-72 right-0 z-0 h-[520px] w-[620px] rounded-full bg-accent/5 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-64 left-1/3 z-0 h-[440px] w-[540px] rounded-full bg-accent/[0.04] blur-3xl"
      />
      {/* Main content */}
      <main className="relative z-10 mt-6 flex min-w-0 flex-1 flex-col gap-8 px-4 pb-16 md:mt-10 md:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 pb-2 pt-1 md:pt-3">
          <h1 className="font-fraunces text-[clamp(34px,4.4vw,48px)] font-light leading-[1.02] tracking-[-0.02em] text-foreground">
            {t('challenges.analysisChallenges')}
          </h1>
          {/* Always offered when logged out (it routes through loggedInAction
              and opens login); when logged in, shown once there are challenges
              to roll from or while they load. */}
          {(!isAuthenticated || challenges.length > 0 || isLoading || onlyAssignments) && (
            <div className="hidden shrink-0 items-center gap-3 md:flex">
              <Button
                variant="outline"
                onClick={() => loggedInAction('help-me-pick', () => setHelpOpen(true))}
                disabled={isLoading || onlyAssignments}
                className="items-center gap-2"
              >
                <Sparkles className="h-5 w-5" />
                {t('challenges.helpMePick.button')}
              </Button>
              <Button
                onClick={handleRollChallenge}
                disabled={isRolling || isLoading || onlyAssignments}
                className="items-center gap-2"
              >
                {isRolling ? (
                  <StandardLoader size="sm" inline />
                ) : (
                  <img src="/dice-icon.svg" alt="" className="h-7 w-7" />
                )}
                {t('challenges.randomChallenge')}
              </Button>
            </div>
          )}
        </div>

        {/* Resume strip and controls share one wrapping row: resume at the
            start, controls pushed to the end (ms-auto). Each takes the width
            its content needs and wraps to a second row when space runs out.
            Shown during loading too so grouping/filters stay usable. */}
        {(challenges.length > 0 || isLoading || onlyAssignments) && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <ResumeStrip challenges={challenges} onResume={startChallenge} onShowAll={browser.showOnlyInProgress} />
            <div className="flex flex-wrap items-end gap-3 ms-auto">
              {/* Grouping — small title above a simple connected segment control.
                  Selected and hover both read as primary text; no fills. */}
              <div className={`flex flex-col gap-1 ${onlyAssignments ? 'pointer-events-none opacity-50' : ''}`}>
                <span className="ps-1 text-xs font-medium text-muted-foreground">{t('challenges.grouping')}</span>
                <div className="inline-flex h-7 items-stretch rounded-lg border border-border" role="group">
                  {GROUP_OPTIONS.map(({ value, key, Icon }, index) => {
                    const active = browser.state.group === value
                    return (
                      <button
                        key={value}
                        type="button"
                        title={t(key)}
                        aria-label={t(key)}
                        aria-pressed={active}
                        disabled={onlyAssignments}
                        onClick={() => browser.setGroup(value)}
                        className={`flex items-center gap-1.5 px-2.5 text-xs transition-colors first:rounded-s-lg last:rounded-e-lg ${index > 0 ? 'border-s border-border' : ''} ${active ? 'font-semibold text-primary' : 'font-medium text-muted-foreground hover:text-primary'}`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="hidden sm:inline">{t(key)}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Filter toggle — pushes the side panel open (desktop) */}
              <Button
                variant={filtersOpen ? 'secondary' : 'outline'}
                size="sm"
                onClick={toggleFilters}
                disabled={onlyAssignments}
                aria-pressed={filtersOpen}
                className="hidden items-center gap-2 md:inline-flex"
              >
                <Funnel className="h-4 w-4" />
                {t('challenges.filters')}
                {browser.activeFilterCount > 0 && (
                  <span className="rounded-full bg-accent/15 px-1.5 font-mono text-xs text-accent">
                    {browser.activeFilterCount}
                  </span>
                )}
              </Button>

              {/* Filter sheet — overlay variant for mobile */}
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" disabled={onlyAssignments} className="inline-flex items-center gap-2 md:hidden">
                    <Funnel className="h-4 w-4" />
                    {t('challenges.filters')}
                    {browser.activeFilterCount > 0 && (
                      <span className="rounded-full bg-accent/15 px-1.5 font-mono text-xs text-accent">
                        {browser.activeFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" hideCloseButton className="w-[300px] overflow-y-auto p-4">
                  <ChallengeFilterRail browser={browser} onClose={() => setMobileFiltersOpen(false)} assignmentsCount={assignments.length} />
                </SheetContent>
              </Sheet>
            </div>
          </div>
        )}

        {showLoading && isLoading && (
          <ChallengeGridSkeleton group={browser.state.group} />
        )}

        {/* Grid — Assignments category first, then the grouped challenges */}
        {(challenges.length > 0 || (assignments.length > 0 && browser.showAssignments)) && (
          <div className="flex flex-col gap-12">
            {assignments.length > 0 && browser.showAssignments && (
              <section className="space-y-6">
                <GroupHeading
                  icon={ClipboardList}
                  title={t('challenges.assignmentsGroup')}
                  count={assignments.length}
                  collapsed={collapsed.has(ASSIGNMENTS_KEY)}
                  onToggle={() => toggleCollapsed(ASSIGNMENTS_KEY, 'assignments')}
                  collapseLabel={t(collapsed.has(ASSIGNMENTS_KEY) ? 'common.expand' : 'common.collapse')}
                />
                {!collapsed.has(ASSIGNMENTS_KEY) && (
                  <div className="grid justify-start gap-6 grid-cols-1 sm:[grid-template-columns:repeat(auto-fill,22rem)]">
                    {assignments.map((assignment) => (
                      <AssignmentChallengeCard
                        key={assignment.assignmentId}
                        assignment={assignment}
                        onOpenDetail={openAssignmentDrawer}
                        active={openAssignmentId === assignment.assignmentId}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {challenges.length > 0 && (
              browser.visibleOrder.length === 0 ? (
                // Don't nag about "no challenges" when the Assignments category is
                // already filling the view (e.g. the Assignments status filter).
                !(assignments.length > 0 && browser.showAssignments) && (
                  <NoDataEmptyState
                    icon={SearchX}
                    title={t('challenges.noMatchTitle')}
                    description={t('challenges.noMatchSubtitle')}
                    buttonText={t('challenges.clearFilters')}
                    onButtonClick={browser.clearAll}
                  />
                )
              ) : (
                browser.groups.map((group) => (
                  <section key={group.key} className="space-y-6">
                    <GroupHeading
                      icon={group.icon}
                      iconClassName={group.iconClassName}
                      title={t(group.labelKey)}
                      count={group.items.length}
                      collapsed={collapsed.has(group.key)}
                      onToggle={() => toggleCollapsed(group.key, browser.state.group)}
                      collapseLabel={t(collapsed.has(group.key) ? 'common.expand' : 'common.collapse')}
                    />
                    {!collapsed.has(group.key) && (
                      <div className="grid justify-start gap-6 grid-cols-1 sm:[grid-template-columns:repeat(auto-fill,22rem)]">
                        {group.items.map((challenge) => (
                          <ChallengeCard
                            key={browser.identity(challenge)}
                            challenge={challenge}
                            onOpenDetail={openDrawer}
                            active={openId !== null && browser.identity(challenge) === openId}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                ))
              )
            )}
          </div>
        )}

        {/* Empty State — only when there are neither challenges nor assignments */}
        {!isLoading && challenges.length === 0 && assignments.length === 0 && (
          <>
            {!isAuthenticated ? (
              <LoginRequiredEmptyState />
            ) : (
              <NoDataEmptyState />
            )}
          </>
        )}
      </main>

      {/* Right-side filter column that pushes the grid (desktop). Available
          during loading too so the Filters button stays functional. */}
      {(challenges.length > 0 || isLoading) && (
        <ChallengeFilterPanel open={filtersOpen} browser={browser} onClose={toggleFilters} assignmentsCount={assignments.length} />
      )}

      <ChallengeDetailDrawer
        item={drawerItem}
        prevItem={drawerPrev}
        nextItem={drawerNext}
        open={drawerOpen}
        position={drawerPosition}
        total={drawerTotal}
        onClose={closeDrawer}
        onNavigate={navigateDrawer}
        onStart={startDrawerItem}
        onLogin={openLoginDialog}
      />

      {assignmentTimeLimitDialog}

      <HelpMePickWizard challenges={challenges} open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}
